import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar, Footer } from "@/components/Layout";
import { CheckCircleIcon, CircleNotchIcon, XCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";

export default function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "polling");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let active = true;
    const poll = async (n) => {
      if (!active) return;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        /* ignore */
      }
      if (n >= 8) {
        setState("timeout");
        return;
      }
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => {
      active = false;
    };
  }, [sessionId, cancelled]);

  const config = {
    polling: { icon: <CircleNotchIcon size={64} className="text-gold-600 animate-spin" />, title: "Confirming your payment…", sub: "Hang tight, this only takes a moment." },
    paid: { icon: <CheckCircleIcon size={64} weight="fill" className="text-green-600" />, title: "Payment received!", sub: "Thank you. A receipt is on its way and your invoice has been updated." },
    cancelled: { icon: <XCircleIcon size={64} weight="fill" className="text-slate-400" />, title: "Payment cancelled", sub: "No charge was made. You can pay any time from your tracking page." },
    timeout: { icon: <WarningCircleIcon size={64} weight="fill" className="text-gold-600" />, title: "Still processing", sub: "Your payment is taking longer than usual. Check your tracking page shortly." },
  }[state];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-20">
        <div data-testid="payment-result" data-state={state} className="bg-white border border-slate-200 rounded-sm p-10 max-w-md w-full text-center shadow-crisp">
          <div className="flex justify-center mb-6">{config.icon}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{config.title}</h1>
          <p className="text-slate-600 mt-3">{config.sub}</p>
          <Link to="/track" className="mt-7 inline-block rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-6 py-3 transition-colors active:scale-95">
            Back to tracking
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
